/**
 * Barbed wire (02 §9.2): "Crossing: 1 wound + 3 s + noise 10 m". Wire stays a wall to the
 * garrison and to the nav grid. The ranger alone may force it: lean into a run for a moment
 * and he goes in - wounded, tangled, heard - and is let out on the far side when he tears
 * free. `ranger/movement.ts` holds him still while `wireTicks > 0`; this module moves him.
 */

import { makeNoise } from '../alert';
import type { Ctx } from '../ctx';
import { woundRanger } from '../damage';
import { resolveMovement } from '../statics';
import { HAZARDS, RANGER } from '../tuning';

/** The far side of the wire run the ranger is pushing into, or null if he is not forcing any. */
function forcedWire(ctx: Ctx): { x: number; z: number } | null {
  const { world: w, statics, cmd } = ctx;
  const r = w.ranger;
  const len = Math.sqrt(cmd.moveX * cmd.moveX + cmd.moveZ * cmd.moveZ);
  if (len < 0.3) return null;
  for (const c of statics.colliders) {
    if (c.shape !== 'box' || !c.blocksMove) continue;
    const nx = Math.min(c.x + c.hx, Math.max(c.x - c.hx, r.x));
    const nz = Math.min(c.z + c.hz, Math.max(c.z - c.hz, r.z));
    const dx = nx - r.x;
    const dz = nz - r.z;
    const d = Math.sqrt(dx * dx + dz * dz);
    // Touching it, and walking into it rather than along it.
    if (d > RANGER.radius + 0.08 || d < 1e-6) continue;
    if ((dx * cmd.moveX + dz * cmd.moveZ) / (d * len) < 0.5) continue;
    if (statics.def.props.find((p) => p.id === c.propId)?.kind !== 'wire') continue;
    // Through the thin way only: nobody crawls the length of a wire run from its end.
    const acrossZ = Math.abs(dz) >= Math.abs(dx);
    if ((acrossZ ? c.hz : c.hx) > HAZARDS.wireMaxDepth) continue;
    const out = (acrossZ ? c.hz : c.hx) + RANGER.radius + 0.05;
    const exit = acrossZ
      ? { x: r.x, z: c.z + (r.z < c.z ? out : -out) }
      : { x: c.x + (r.x < c.x ? out : -out), z: r.z };
    // The far side must be open ground: a wall, a second apron or the map edge turns him back.
    const open = resolveMovement(statics, exit.x, exit.z, RANGER.radius);
    if (Math.abs(open.x - exit.x) > 0.02 || Math.abs(open.z - exit.z) > 0.02) continue;
    return exit;
  }
  return null;
}

export function stepWire(ctx: Ctx): void {
  const w = ctx.world;
  const r = w.ranger;
  const h = w.hazards;

  if (h.wireTicks > 0) {
    // Struggling through: he works his way across as the seconds run out, and comes free on the far side.
    if (h.wireExit) {
      r.x += (h.wireExit.x - r.x) / h.wireTicks;
      r.z += (h.wireExit.z - r.z) / h.wireTicks;
    }
    h.wireTicks--;
    if (h.wireTicks === 0) h.wireExit = null;
    return;
  }

  // Hands full or already down: the wire simply stops him, as it stops everyone else.
  const exit = r.status === 'active' && r.dragging < 0 ? forcedWire(ctx) : null;
  if (!exit) {
    h.wirePushTicks = 0;
    return;
  }
  h.wirePushTicks++;
  if (h.wirePushTicks < HAZARDS.wirePushTicks) return;
  h.wirePushTicks = 0;
  h.wireExit = exit;
  if (w.mods.wireImmune) {
    // The perk: he slips through, unhurt and unheard.
    h.wireTicks = HAZARDS.wireImmuneTicks;
    return;
  }
  h.wireTicks = HAZARDS.wireTangleTicks;
  ctx.events.push({ type: 'rangerHit', wounded: true });
  woundRanger(ctx, 1, 'wire');
  // Cursing and twanging wire: men close by come to look, but it is no alarm.
  makeNoise(ctx, r.x, r.z, HAZARDS.wireNoise, 'shout');
}
